import {
  Component,
  forwardRef,
  ContentChildren,
  ChangeDetectionStrategy,
  ElementRef, Input, OnChanges, QueryList, SimpleChanges, ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgModel, ValidationErrors, Validator
} from '@angular/forms';
import { NgClass, NgTemplateOutlet } from '@angular/common';

/**
 * interfaces
*/
import { IFormFieldComponentProps } from '@sharedModules/forms/interfaces/form-field-component-props';

/**
 * enums
*/
import { ISwitchSize } from './enums/switch-size';
import { IThemeColorVariant } from '@core/enums/theme-colors-variants';
import { CheckType, ICheckType } from '@sharedModules/forms/components/check/enums/check-type';
import { FormFieldStatus } from '@sharedModules/forms/directives/form-field-status/enums/form-field-status';

/**
 * utils
*/
import { SharedUtils } from '@shared/utils/shared-utils';

/**
 * models
*/
import { FormCheckFieldModel } from '@sharedModules/forms/models/form-check-field/form-check-field-model';

/**
 * directives
*/
import { TemplateDirective } from '@shared/directives/templates/template.directive';
import { FormFieldStatusDirective } from '@sharedModules/forms/directives/form-field-status/form-field-status.directive';

/**
 * @component SwitchComponent
 * @description A customizable switch/toggle component that extends FormCheckFieldModel and implements form control functionality.
 * 
 * @example
 * ```html
 * <app-switch
 *   identifier="mySwitch"
 *   name="mySwitch"
 *   label="Enable Feature"
 *   variant="primary"
 *   size="lg"
 *   [checked]="true"
 *   [disabled]="false"
 *   [required]="true">
 * </app-switch>
 * ```
 * 
 * @example
 * ```html
 * <app-switch
 *   identifier="outlineSwitch"
 *   name="outlineSwitch"
 *   label="Outline Style"
 *   variant="success"
 *   [outline]="true"
 *   [square]="true"
 *   iconOn="fa fa-check"
 *   iconOff="fa fa-times">
 * </app-switch>
 * ```
 * 
 * @implements {IFormFieldComponentProps} - Interface for form field component properties
 * @implements {Validator} - Angular form validator interface
 * @extends {FormCheckFieldModel} - Base class for form check field functionality
 * 
 * @selector app-switch
 * @changeDetection OnPush
 * @providers NG_VALUE_ACCESSOR, NG_VALIDATORS
 * @hostClass app-switch
 */
@Component({
  selector: 'app-switch',
  templateUrl: './switch.component.html',
  styleUrl: './switch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true
    }
  ],
  host: {
    class: 'app-switch',
  },
  imports: [
    NgClass,
    NgTemplateOutlet,
    /**
     * directives
     */
    FormFieldStatusDirective,
  ],
})
export class SwitchComponent extends FormCheckFieldModel
  implements IFormFieldComponentProps, Validator, OnChanges {

  /**
   * 
  */
  public ngOnChanges(changes: SimpleChanges): void {
    if ('checked' in changes && this.control == null) {
      this._innerValue = this.checked;
    }
  }

  /**
   * @property templates
   * @description QueryList of TemplateDirective instances for custom template injection.
   * Allows users to provide custom templates for switchOn, switchOff, switchLabel, and textHelp.
   * 
   * @type {QueryList<TemplateDirective>}
   * @decorator ContentChildren
   * 
   * @example
   * ```html
   * <app-switch identifier="custom" name="custom">
   *   <ng-template appTemplate="switchOn" let-icon>
   *     <i [ngClass]="icon" class="custom-on-icon"></i>
   *   </ng-template>
   * </app-switch>
   * ```
   */
  @ContentChildren(TemplateDirective)
  public templates!: QueryList<TemplateDirective>

  /**
   * @property inputElementRef
   * @description Reference to the underlying HTML input element for programmatic access.
   * 
   * @type {ElementRef<HTMLInputElement>}
   * @decorator ViewChild
   * @static true
   * 
   * @example
   * ```typescript
   * @ViewChild('switchComponent') switchComponent!: SwitchComponent;
   * 
   * focusSwitch() {
   *   this.switchComponent.inputElementRef.nativeElement.focus();
   * }
   * ```
   */
  @ViewChild('inputRef', { static: true })
  public inputElementRef!: ElementRef<HTMLInputElement>

  /**
   * @property square
   * @description Determines whether the switch should have a square appearance instead of the default rounded style.
   * 
   * @type {boolean}
   * @default false
   * @decorator Input
   * @alias square
   * 
   * @example
   * ```html
   * <app-switch [square]="true" identifier="squareSwitch" name="squareSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'square' })
  public square: boolean = false;

  /**
   * @property iconOn
   * @description CSS class(es) for the icon displayed when the switch is in the ON/checked state.
   * 
   * @type {string}
   * @default 'fa fa-check'
   * @decorator Input
   * @alias iconOn
   * 
   * @example
   * ```html
   * <app-switch iconOn="fas fa-toggle-on" identifier="iconSwitch" name="iconSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'iconOn' })
  public iconOn: string = 'fa fa-check';

  /**
   * @property iconOff
   * @description CSS class(es) for the icon displayed when the switch is in the OFF/unchecked state.
   * 
   * @type {string}
   * @default 'fa fa-x'
   * @decorator Input
   * @alias iconOff
   * 
   * @example
   * ```html
   * <app-switch iconOff="fas fa-toggle-off" identifier="iconSwitch" name="iconSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'iconOff' })
  public iconOff: string = 'fa fa-x';

  /**
   * @property variant
   * @description Bootstrap theme color variant for styling the switch (e.g., 'primary', 'success', 'danger').
   * 
   * @type {IThemeColorVariant}
   * @decorator Input
   * @alias variant
   * 
   * @example
   * ```html
   * <app-switch variant="success" identifier="successSwitch" name="successSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'variant' })
  public variant!: IThemeColorVariant;

  /**
   * @property size
   * @description Size of the switch component. Can be 'sm' (small) or 'lg' (large).
   * 
   * @type {ISwitchSize}
   * @decorator Input
   * @alias size
   * 
   * @example
   * ```html
   * <app-switch size="lg" identifier="largeSwitch" name="largeSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'size' })
  public size!: ISwitchSize;
  
  /**
   * @property outline
   * @description When true, applies an outlined style to the switch instead of the filled style.
   * 
   * @type {boolean}
   * @default false
   * @decorator Input
   * @alias outline
   * 
   * @example
   * ```html
   * <app-switch [outline]="true" variant="primary" identifier="outlineSwitch" name="outlineSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'outline' })
  public outline: boolean = false;

  /**
   * @property type
   * @description HTML input type for the switch. Defaults to checkbox type.
   * 
   * @type {ICheckType}
   * @default CheckType.Checkbox
   * @decorator Input
   * @alias type
   * 
   * @example
   * ```html
   * <app-switch [type]="CheckType.Radio" identifier="radioSwitch" name="radioSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'type' })
  public type: ICheckType = CheckType.Checkbox;

  /**
   * @property value
   * @description The value associated with the switch when it's checked.
   * 
   * @type {unknown}
   * @decorator Input
   * @alias value
   * 
   * @example
   * ```html
   * <app-switch value="enabled" identifier="valueSwitch" name="valueSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'value' })
  public value!: unknown;

  /**
   * @property checked
   * @description Whether the switch is currently in the checked/ON state.
   * 
   * @type {boolean}
   * @default false
   * @decorator Input
   * @alias checked
   * 
   * @example
   * ```html
   * <app-switch [checked]="isEnabled" identifier="checkedSwitch" name="checkedSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'checked' })
  public checked: boolean = false;

  /**
   * @property id
   * @description Unique identifier for the switch input element. Used for accessibility and form association.
   * 
   * @type {string}
   * @required true
   * @decorator Input
   * @alias identifier
   * 
   * @example
   * ```html
   * <app-switch identifier="uniqueSwitchId" name="switchName">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'identifier', required: true })
  public id!: string;

  /**
   * @property name
   * @description Name attribute for the switch input element. Used in form submission.
   * 
   * @type {string}
   * @required true
   * @decorator Input
   * @alias name
   * 
   * @example
   * ```html
   * <app-switch identifier="switchId" name="featureToggle">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'name', required: true })
  public name!: string;

  /**
   * @property readonly
   * @description When true, the switch becomes read-only and cannot be interacted with.
   * 
   * @type {boolean}
   * @default false
   * @decorator Input
   * @alias readonly
   * 
   * @example
   * ```html
   * <app-switch [readonly]="true" identifier="readonlySwitch" name="readonlySwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'readonly' })
  public readonly: boolean = false;

  /**
   * @property disabled
   * @description When true, the switch becomes disabled and cannot be interacted with.
   * 
   * @type {boolean}
   * @default false
   * @decorator Input
   * @alias disabled
   * 
   * @example
   * ```html
   * <app-switch [disabled]="isDisabled" identifier="disabledSwitch" name="disabledSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'disabled' })
  public disabled: boolean = false;

  /**
   * @property required
   * @description When true, the switch becomes a required field in form validation.
   * 
   * @type {boolean}
   * @default false
   * @decorator Input
   * @alias required
   * 
   * @example
   * ```html
   * <app-switch [required]="true" identifier="requiredSwitch" name="requiredSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'required' })
  public required: boolean = false;

  /**
   * @property label
   * @description Text label displayed alongside the switch component.
   * 
   * @type {string}
   * @default ''
   * @decorator Input
   * @alias label
   * 
   * @example
   * ```html
   * <app-switch label="Enable Notifications" identifier="labelSwitch" name="labelSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'label' })
  public label: string = '';

  /**
   * @property ariaLabel
   * @description ARIA label for accessibility purposes. Provides screen reader support.
   * 
   * @type {string | undefined}
   * @decorator Input
   * @alias ariaLabel
   * 
   * @example
   * ```html
   * <app-switch ariaLabel="Toggle dark mode" identifier="ariaSwitch" name="ariaSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'ariaLabel' })
  public ariaLabel: string | undefined;

  /**
   * @property described
   * @description ID reference for ARIA description element. Links the switch to descriptive text.
   * 
   * @type {string | undefined}
   * @decorator Input
   * @alias ariaDescribed
   * 
   * @example
   * ```html
   * <div id="switchDescription">This switch controls the application theme</div>
   * <app-switch ariaDescribed="switchDescription" identifier="describedSwitch" name="describedSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'ariaDescribed' })
  public described: string | undefined

  /**
   * @property labelClassStyle
   * @description CSS class(es) to apply to the switch label for custom styling.
   * 
   * @type {string | string[] | undefined}
   * @decorator Input
   * @alias labelClassStyle
   * 
   * @example
   * ```html
   * <app-switch labelClassStyle="text-bold text-primary" identifier="styledSwitch" name="styledSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'labelClassStyle' })
  public labelClassStyle: string | string[] | undefined;

  /**
   * @property textHelp
   * @description Helper text displayed below the switch to provide additional context or instructions.
   * 
   * @type {string | undefined}
   * @decorator Input
   * @alias textHelp
   * 
   * @example
   * ```html
   * <app-switch textHelp="This will enable push notifications for all updates" identifier="helpSwitch" name="helpSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'textHelp' })
  public textHelp: string | undefined

  /**
   * @property textHelpClassStyle
   * @description CSS class(es) to apply to the help text for custom styling.
   * 
   * @type {string | string[]}
   * @default ''
   * @decorator Input
   * @alias textHelpClassStyle
   * 
   * @example
   * ```html
   * <app-switch textHelpClassStyle="text-muted small" identifier="styledHelpSwitch" name="styledHelpSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'textHelpClassStyle' })
  public textHelpClassStyle: string | string[] = ''

  /**
   * @property ignoreStatus
   * @description Form field status to ignore during validation. Controls when validation styling is applied.
   * 
   * @type {FormFieldStatus}
   * @default FormFieldStatus.Valid
   * @decorator Input
   * @alias ignoreStatus
   * 
   * @example
   * ```html
   * <app-switch [ignoreStatus]="FormFieldStatus.Invalid" identifier="statusSwitch" name="statusSwitch">
   * </app-switch>
   * ```
   */
  @Input({ alias: 'ignoreStatus' })
  public ignoreStatus: FormFieldStatus = FormFieldStatus.Valid

  /**
   * @getter switchClassStyle
   * @description Generates CSS classes for the switch based on its configuration (variant, size, outline).
   * 
   * @returns {string} Space-separated CSS class names for styling the switch
   * 
   * @example
   * ```typescript
   * // Returns: "switch-primary switch-lg"
   * const classes = this.switchClassStyle;
   * ```
   * 
   * @example
   * ```typescript
   * // Returns: "switch-outline-success switch-sm"
   * this.outline = true;
   * this.variant = 'success';
   * this.size = 'sm';
   * const classes = this.switchClassStyle;
   * ```
   */
  public get switchClassStyle(): string {

    // initialize the classes array
    const classes = [];

    // if the switch is outlined
    if (this.outline) {
      
      if (this.variant) classes.push(SharedUtils.generateVariantClassStyle(this.variant, 'switch-outline'));
      else classes.push('switch-outline');

    }
    // if the switch is not outlined
    else if (this.variant) {
      classes.push(SharedUtils.generateVariantClassStyle(this.variant, 'switch'));
    }

    // if the switch has a size
    if (this.size) classes.push(SharedUtils.generateSizeClassStyle(this.size, 'switch'));

    return classes.join(' ');

  }

  /**
   * @method validate
   * @description Validates the switch value according to Angular form validation rules.
   * Implements the Validator interface for form integration.
   * 
   * @param {AbstractControl} control - The form control to validate
   * @returns {ValidationErrors | null} Validation errors object or null if validation passes
   * 
   * @example
   * ```typescript
   * // In a reactive form
   * const switchControl = new FormControl(false, [Validators.required]);
   * const errors = this.switchComponent.validate(switchControl);
   * // Returns: { required: true } if control is required and unchecked
   * ```
   * 
   * @example
   * ```typescript
   * // In a template-driven form
   * <app-switch name="requiredSwitch" [(ngModel)]="isEnabled" required>
   * </app-switch>
   * // Validation will automatically run when the form is submitted
   * ```
   */
  public validate(control: AbstractControl): ValidationErrors | null {

    // if the control is a model, return null
    if (!(this.ngControl instanceof NgModel)) return null;

    // if the control is required and the value is false, return an error 
    if (this.required && !control.value) {
      return { required: true };
    }

    // return null if the control is not required or the value is true
    return null;

  }

  /**
   * @method getTemplate
   * @description Retrieves a custom template by its name from the available templates.
   * Used to access custom templates provided via TemplateDirective for switchOn, switchOff, switchLabel, and textHelp.
   * 
   * @param {string} tempName - The name of the template to retrieve
   * @returns {TemplateDirective | undefined} The template directive if found, undefined otherwise
   * 
   * @example
   * ```typescript
   * // Get custom switch on template
   * const switchOnTemplate = this.getTemplate('switchOn');
   * if (switchOnTemplate) {
   *   // Use the custom template
   *   console.log('Custom switch on template found');
   * }
   * ```
   * 
   * @example
   * ```html
   * <!-- In template -->
   * @if (getTemplate('switchOn'); as switchOnTpl) {
   *   <ng-container *ngTemplateOutlet="switchOnTpl.templateRef; context: { $implicit: iconOn }" />
   * }
   * ```
   */
  public getTemplate(tempName: string): TemplateDirective | undefined {
    return this.templates.find(temp => temp.name == tempName);
  }

}